import { useEffect, useRef, useState } from "react";
import {
  MessageSquareText, Plus, Pencil, Trash2, Search, Loader2, Settings2, Hash,
  Maximize2, Minimize2,
} from "lucide-react";
import { Button, Input, Label, Modal, ModalFooter, Textarea, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * A canned response / snippet. `body` is stored verbatim — it may contain
 * merge tokens like {{first_name}} that are resolved at send time, so the
 * composer inserts the raw text and never tries to interpolate it here.
 */
export interface Snippet {
  id: string;
  title: string;
  shortcut: string | null;
  body: string;
  /** email | sms | null (null = available on every channel) */
  channel: string | null;
  usage_count: number;
  // A saved reply that is ALSO the booking's own confirmation. Empty
  // anywhere means "any kind of visit".
  auto_confirm?: boolean;
  confirm_packets?: string[] | null;
  sms_body?: string | null;
  confirm_mode?: string | null;
  confirm_status?: string | null;
  email_subject?: string | null;
}

/** Her packets, in the order the service editor lists them. */
const PACKETS: { value: string; label: string }[] = [
  { value: "iv", label: "IV / drips" },
  { value: "nad", label: "NAD+" },
  { value: "weight_loss", label: "Weight loss" },
  { value: "injections", label: "Wellness injections" },
  { value: "peptides", label: "Peptides" },
  { value: "testosterone", label: "Testosterone" },
];

type Channel = "email" | "sms";

interface ReplyLinks {
  review_url: string | null;
  booking_url: string | null;
  /** This client's own page, which lists any paperwork they still owe. */
  forms_url?: string | null;
  /** Her cancellation policy, pasted into Settings. */
  policy_url?: string | null;
  /** This client's next visit, or their last one if nothing is booked. */
  appointment?: { date: string; time: string; when: string; service: string } | null;
  /** Where this client adds or replaces the card that holds their visit. */
  card_url?: string | null;
  /** "Visa ending 4242", or null when there isn't one yet. */
  card_on_file?: string | null;
}

/**
 * Fill a saved reply in for one client. The inbox sends the box's text
 * as-is, so the fill-ins must be resolved here; a missing link shows as a
 * bracketed note she can't miss before pressing Send.
 */
export function fillReply(
  body: string,
  contact: { first_name: string | null; last_name: string | null },
  links: ReplyLinks | null,
): string {
  const first = contact.first_name?.trim() || "there";
  const full = [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() || first;
  return body
    .replace(/\{\{\s*first_name\s*\}\}/gi, first)
    .replace(/\{\{\s*last_name\s*\}\}/gi, contact.last_name?.trim() ?? "")
    .replace(/\{\{\s*full_name\s*\}\}/gi, full)
    .replace(/\{\{\s*review_link\s*\}\}/gi, links?.review_url || "[add your Google review link in Settings]")
    .replace(/\{\{\s*booking_link\s*\}\}/gi, links?.booking_url || "[booking link]")
    // "Can the system auto populate the actual service date and time into the
    // generated response?" — their next visit, filled in the moment the reply
    // is inserted, so what's in the box is what will send. No visit booked
    // shows as a bracket she can't miss rather than an empty gap.
    .replace(/\{\{\s*appointment_date\s*\}\}/gi, links?.appointment?.date || "[no visit booked]")
    .replace(/\{\{\s*appointment_time\s*\}\}/gi, links?.appointment?.time || "[no visit booked]")
    .replace(/\{\{\s*appointment_when\s*\}\}/gi, links?.appointment?.when || "[no visit booked]")
    .replace(/\{\{\s*appointment_service\s*\}\}/gi, links?.appointment?.service || "[no visit booked]")
    // The two links her confirmation wording asks for by name.
    .replace(/\{\{\s*forms_link\s*\}\}/gi, links?.forms_url || "[form link]")
    .replace(/\{\{\s*cancellation_policy\s*\}\}/gi, links?.policy_url || "[add your cancellation policy link in Settings]")
    // "A credit card is required to secure and hold your appointment."
    .replace(/\{\{\s*card_link\s*\}\}/gi, links?.card_url || "[card link]");
}

/**
 * Reusable canned-responses dropdown. Renders its own trigger button; the
 * host just supplies the channel to scope by and an `onInsert` callback that
 * receives the chosen snippet's raw body (tokens included, unresolved).
 *
 * Clicking a snippet fires POST /snippets/:id/use fire-and-forget so usage
 * counts stay warm without blocking the insert. A "Manage" affordance opens
 * an inline CRUD modal.
 */
export function SnippetPicker({
  channel,
  onInsert,
  contact,
  placement = "down",
  className,
}: {
  channel?: Channel;
  onInsert: (body: string) => void;
  /** Fill the reply in for this client before it lands in the box, so what
   *  she sees is exactly what sends. Without it the raw text is inserted. */
  contact?: { id: string; first_name: string | null; last_name: string | null } | null;
  /** "up" when the picker sits at the bottom of the screen (the message box). */
  placement?: "up" | "down";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ snippets: Snippet[] }>("/snippets", { channel });
      setSnippets(d.snippets ?? []);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load saved replies");
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  // Fetch the first time the dropdown opens; refetches happen after the
  // manage modal saves changes (see onChanged).
  useEffect(() => {
    if (open && !loaded) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loaded]);

  // Dismiss the dropdown on outside-click / Escape — same pattern as the
  // mail snooze popover. The manage modal handles its own dismissal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const [links, setLinks] = useState<ReplyLinks | null>(null);
  const loadLinks = async (): Promise<ReplyLinks> => {
    try {
      const l = await api.get<ReplyLinks>("/snippets/links", contact ? { contact_id: contact.id } : undefined);
      setLinks(l);
      return l;
    } catch {
      return { review_url: null, booking_url: null, forms_url: null, policy_url: null, appointment: null };
    }
  };
  useEffect(() => {
    if (open && contact && !links) void loadLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact, links]);

  const insert = async (s: Snippet) => {
    // A click can beat the first links fetch; wait for it rather than
    // dropping a placeholder into a real message.
    const l = contact ? (links ?? await loadLinks()) : null;
    onInsert(contact ? fillReply(s.body, contact, l) : s.body);
    // Fire-and-forget usage bump — never blocks or surfaces errors.
    api.post(`/snippets/${s.id}/use`).catch(() => {});
    setSnippets((prev) => prev.map((x) => x.id === s.id ? { ...x, usage_count: x.usage_count + 1 } : x));
    setOpen(false);
    setQuery("");
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? snippets.filter((s) =>
        s.title.toLowerCase().includes(q) ||
        (s.shortcut ?? "").toLowerCase().includes(q) ||
        s.body.toLowerCase().includes(q))
    : snippets;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border text-xs font-medium transition-colors",
          open
            ? "border-indigo-500/50 bg-indigo-500/15 text-indigo-200"
            : "border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:text-zinc-100 hover:border-zinc-700"
        )}
        aria-label="Insert a saved reply"
      >
        <MessageSquareText className="h-3.5 w-3.5" />
        Saved replies
      </button>

      {open && (
        <div className={cn(
          "absolute right-0 z-50 w-80 rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/60 animate-fade-in overflow-hidden",
          placement === "up" ? "bottom-full mb-1.5" : "top-full mt-1.5",
        )}>
          <div className="p-2 border-b border-zinc-900">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search saved replies…"
                className="w-full pl-7 pr-2 h-8 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-zinc-500">
                {snippets.length === 0
                  ? "No saved replies yet. Add your go-to answers once, then drop them into any message."
                  : "No saved replies match your search."}
              </div>
            ) : (
              filtered.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => void insert(s)}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-900 transition-colors group"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-100 truncate flex-1">{s.title}</span>
                    {s.shortcut && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-500 shrink-0">
                        <Hash className="h-2.5 w-2.5" />{s.shortcut}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-[11px] text-zinc-500 line-clamp-2">{s.body}</p>
                </button>
              ))
            )}
          </div>

          <div className="border-t border-zinc-900 p-1.5">
            <button
              type="button"
              onClick={() => { setManageOpen(true); setOpen(false); }}
              className="w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100 transition-colors"
            >
              <Settings2 className="h-3.5 w-3.5 text-zinc-500" />
              Add or edit saved replies
            </button>
          </div>
        </div>
      )}

      {manageOpen && (
        <SnippetManagerModal
          defaultChannel={channel}
          onClose={() => setManageOpen(false)}
          onChanged={() => { setLoaded(false); if (open) void load(); }}
        />
      )}
    </div>
  );
}

// ─── Manage modal ────────────────────────────────────────────────────────────
// Full CRUD in one place: the list on the left of the create/edit form. Kept
// deliberately compact so it reads like the rest of the app's dialogs.

function SnippetManagerModal({
  defaultChannel,
  onClose,
  onChanged,
}: {
  defaultChannel?: Channel;
  onClose: () => void;
  onChanged: () => void;
}) {
  // "The saved replies can we have option to open that box bigger" - her
  // confirmation emails run past two thousand characters. Remembered, because
  // somebody who wants the big box once wants it every time; storage can throw
  // in a locked-down browser, so it never decides whether the editor renders.
  const [roomy, setRoomyState] = useState(() => {
    try { return localStorage.getItem("hh.snippetEditor.roomy") === "1"; } catch { return false; }
  });
  const setRoomy = (next: boolean | ((v: boolean) => boolean)) => {
    setRoomyState((prev) => {
      const v = typeof next === "function" ? next(prev) : next;
      try { localStorage.setItem("hh.snippetEditor.roomy", v ? "1" : "0"); } catch { /* fine */ }
      return v;
    });
  };

  const toast = useToast();
  const confirm = useConfirm();

  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Snippet | "new">("new");

  const load = async () => {
    setLoading(true);
    try {
      // No channel filter here — the manager shows every snippet regardless
      // of which channel it's scoped to.
      const d = await api.get<{ snippets: Snippet[] }>("/snippets");
      setSnippets(d.snippets ?? []);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load saved replies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onDelete = async (s: Snippet) => {
    if (!(await confirm({
      title: `Delete "${s.title}"?`,
      confirmLabel: "Delete",
      danger: true,
    }))) return;
    try {
      await api.delete(`/snippets/${s.id}`);
      setSnippets((prev) => prev.filter((x) => x.id !== s.id));
      if (editing !== "new" && editing.id === s.id) setEditing("new");
      onChanged();
      toast.success("Saved reply deleted");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to delete");
    }
  };

  return (
    <Modal open onClose={onClose} title="Saved replies" size={roomy ? "xl" : "lg"}>
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,11rem)_1fr] gap-4 min-h-[22rem]">
        {/* List rail */}
        <div className="flex flex-col gap-1.5 sm:border-r sm:border-zinc-800 sm:pr-4">
          <button
            type="button"
            onClick={() => setEditing("new")}
            className={cn(
              "flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors",
              editing === "new"
                ? "bg-indigo-500/15 text-indigo-200"
                : "text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
            )}
          >
            <Plus className="h-3.5 w-3.5" /> New saved reply
          </button>
          <div className="flex-1 overflow-y-auto -mr-1 pr-1 max-h-72 space-y-0.5">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : snippets.length === 0 ? (
              <p className="px-2.5 py-4 text-[11px] text-zinc-500">No saved replies yet.</p>
            ) : (
              snippets.map((s) => {
                const active = editing !== "new" && editing.id === s.id;
                return (
                  <div
                    key={s.id}
                    className={cn(
                      "group flex items-center gap-1 rounded-lg pl-2.5 pr-1 py-1.5 transition-colors",
                      active ? "bg-zinc-800/70" : "hover:bg-zinc-900"
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setEditing(s)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <span className="block text-xs text-zinc-100 truncate">{s.title}</span>
                      {s.shortcut && (
                        <span className="block text-[10px] text-zinc-500 truncate">#{s.shortcut}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDelete(s)}
                      className="h-6 w-6 grid place-items-center rounded text-zinc-600 hover:text-rose-300 hover:bg-rose-500/10 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Delete ${s.title}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Editor form */}
        <SnippetForm
          key={editing === "new" ? "new" : editing.id}
          target={editing}
          defaultChannel={defaultChannel}
          roomy={roomy}
          setRoomy={setRoomy}
          onSaved={(saved, mode) => {
            setSnippets((prev) => mode === "create"
              ? [saved, ...prev]
              : prev.map((x) => x.id === saved.id ? saved : x));
            setEditing(saved);
            onChanged();
          }}
        />
      </div>
    </Modal>
  );
}

// ─── Create / edit form ──────────────────────────────────────────────────────
// Remounted (via `key`) whenever the selected target changes, so the local
// field state always initialises from the right row without a sync effect.

function SnippetForm({
  target,
  defaultChannel,
  onSaved,
  roomy,
  setRoomy,
}: {
  target: Snippet | "new";
  defaultChannel?: Channel;
  onSaved: (snippet: Snippet, mode: "create" | "edit") => void;
  roomy: boolean;
  setRoomy: (next: boolean | ((v: boolean) => boolean)) => void;
}) {
  const toast = useToast();
  const editing = target === "new" ? null : target;

  const [title, setTitle] = useState(editing?.title ?? "");
  const [shortcut, setShortcut] = useState(editing?.shortcut ?? "");
  const [body, setBody] = useState(editing?.body ?? "");
  // "" = every channel (stored as 'any'). New replies default to every
  // channel: most answers work as a text or an email.
  const [channel, setChannel] = useState<string>(
    editing?.channel && editing.channel !== "any" ? editing.channel : "",
  );
  void defaultChannel;
  // Send this one automatically as the booking confirmation, and for which
  // kind of visit. Null / empty anywhere means "any".
  const [autoConfirm, setAutoConfirm] = useState(editing?.auto_confirm === true);
  const [packets, setPackets] = useState<string[]>(editing?.confirm_packets ?? []);
  const [confirmMode, setConfirmMode] = useState<string>(editing?.confirm_mode ?? "");
  const [confirmStatus, setConfirmStatus] = useState<string>(editing?.confirm_status ?? "");
  const [emailSubject, setEmailSubject] = useState(editing?.email_subject ?? "");
  const [smsBody, setSmsBody] = useState(editing?.sms_body ?? "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    const payload = {
      title: title.trim(),
      shortcut: shortcut.trim() || null,
      body,
      // The API wants 'any' for every channel; sending null made "All
      // channels" fail to save.
      channel: channel || "any",
      auto_confirm: autoConfirm,
      confirm_packets: packets.length > 0 ? packets : null,
      confirm_mode: confirmMode || null,
      confirm_status: confirmStatus || null,
      email_subject: emailSubject.trim() || null,
      sms_body: smsBody.trim() || null,
    };
    try {
      if (editing) {
        const r = await api.patch<{ snippet: Snippet }>(`/snippets/${editing.id}`, payload);
        onSaved(r.snippet, "edit");
        toast.success("Saved reply updated");
      } else {
        const r = await api.post<{ snippet: Snippet }>("/snippets", payload);
        onSaved(r.snippet, "create");
        toast.success("Saved reply added");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="space-y-3 flex-1">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem] gap-3">
          <div>
            <Label required>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Consultation details" autoFocus />
          </div>
          <div>
            <Label>Shortcut</Label>
            <Input
              value={shortcut}
              onChange={(e) => setShortcut(e.target.value)}
              leftIcon={<Hash className="h-3.5 w-3.5" />}
              placeholder="welcome"
            />
          </div>
        </div>
        <div>
          {/* "The saved replies can we have option to open that box bigger" -
              her confirmation emails run to two thousand characters, which is
              a lot to read through a nine-rem window. The box is draggable and
              this makes it tall in one click, and stays that way next time. */}
          <div className="flex items-center justify-between">
            <Label required>Body</Label>
            <button
              type="button"
              onClick={() => setRoomy((v) => !v)}
              className="mb-1 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-300 hover:text-indigo-200"
            >
              {roomy
                ? <><Minimize2 className="h-3 w-3" /> Smaller box</>
                : <><Maximize2 className="h-3 w-3" /> Bigger box</>}
            </button>
          </div>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Hi {{first_name}}, thanks for reaching out…"
            className={roomy ? "min-h-[26rem] resize-y text-sm" : "min-h-[9rem] resize-y text-xs"}
          />
          <p className="mt-1.5 text-[11px] text-zinc-500">
            Fill-ins: <code className="text-zinc-300">{"{{first_name}}"}</code>,{" "}
            <code className="text-zinc-300">{"{{appointment_date}}"}</code>,{" "}
            <code className="text-zinc-300">{"{{appointment_time}}"}</code>,{" "}
            <code className="text-zinc-300">{"{{appointment_when}}"}</code> (date and time together),{" "}
            <code className="text-zinc-300">{"{{appointment_service}}"}</code>,{" "}
            <code className="text-zinc-300">{"{{forms_link}}"}</code> (their own page, listing any paperwork they still owe),{" "}
            <code className="text-zinc-300">{"{{cancellation_policy}}"}</code>,{" "}
            <code className="text-zinc-300">{"{{card_link}}"}</code> (where they add the card that holds their visit),{" "}
            <code className="text-zinc-300">{"{{review_link}}"}</code> (your Google review link, set in Settings) and{" "}
            <code className="text-zinc-300">{"{{booking_link}}"}</code>. The appointment ones use their next visit, or their last one if nothing is booked. They&rsquo;re filled in when you insert the reply, so you see exactly what will send.
          </p>
        </div>
        <div>
          <Label>Channel</Label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none"
          >
            <option value="">All channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>

        {/* She wrote one confirmation for every kind of visit and labelled each
            with the chair and the paperwork. Those labels are three facts the
            booking already knows, so it can pick the right reply itself. */}
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <label className="flex items-start gap-2 text-sm text-zinc-200">
            <input
              type="checkbox"
              checked={autoConfirm}
              onChange={(e) => setAutoConfirm(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-zinc-700 bg-zinc-900"
            />
            <span>
              Send this automatically when a visit is booked
              <span className="mt-0.5 block text-[11px] text-zinc-500">
                It goes out as the client&rsquo;s confirmation email, with the fill-ins already done.
                They also get a text — yours below, or a short one we write.
              </span>
            </span>
          </label>

          {autoConfirm && (
            <div className="mt-3 space-y-3 border-t border-zinc-800/70 pt-3">
              <div>
                <Label>Subject line</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Your appointment is confirmed"
                />
              </div>
              <div>
                {/* "the email response are perfect they way they are this im
                    talking about is a revised text to match the email
                    responses" — her shorter version, for the text message. */}
                <Label>Text message version <span className="text-zinc-600">(optional)</span></Label>
                <Textarea
                  rows={roomy ? 14 : 6}
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  className="resize-y"
                  placeholder="Leave empty and we'll send a short line pointing at the email."
                />
                <p className="mt-1 text-[11px] text-zinc-500">
                  The same fill-ins work here. Texts are charged per 160 characters, so this one costs{" "}
                  {smsBody.trim() ? `about ${Math.max(1, Math.ceil(smsBody.trim().length / 153))} texts to send` : "nothing extra"}.
                </p>
              </div>
              <div>
                <Label>For these treatments</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {PACKETS.map((p) => {
                    const on = packets.includes(p.value);
                    return (
                      <button
                        type="button"
                        key={p.value}
                        onClick={() => setPackets(on ? packets.filter((x) => x !== p.value) : [...packets, p.value])}
                        className={
                          "rounded-full border px-2.5 py-1 text-[11px] font-medium " +
                          (on
                            ? "border-indigo-500/60 bg-indigo-500/15 text-indigo-200"
                            : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200")
                        }
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[11px] text-zinc-500">
                  Pick none to use this for every treatment.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label>How they&rsquo;re seen</Label>
                  <select
                    value={confirmMode}
                    onChange={(e) => setConfirmMode(e.target.value)}
                    className="w-full h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none"
                  >
                    <option value="">Any way</option>
                    <option value="in_person">In the lounge</option>
                    <option value="video">By video</option>
                    <option value="phone">By phone</option>
                  </select>
                </div>
                <div>
                  <Label>Which clients</Label>
                  <select
                    value={confirmStatus}
                    onChange={(e) => setConfirmStatus(e.target.value)}
                    className="w-full h-9 px-3 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 focus:border-indigo-500/50 focus:outline-none"
                  >
                    <option value="">New and returning</option>
                    <option value="new">First visit only</option>
                    <option value="returning">Returning only</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ModalFooter>
        <Button onClick={() => void submit()} loading={saving} disabled={!title.trim() || !body.trim()}>
          {editing
            ? <><Pencil className="h-3.5 w-3.5 mr-1.5" />Save changes</>
            : <><Plus className="h-3.5 w-3.5 mr-1.5" />Save reply</>}
        </Button>
      </ModalFooter>
    </div>
  );
}
